import * as React from 'react';
import './SuggestedAddressConfirmation.scss';
import { render } from 'react-dom';
import './DialogTypes.scss';

/**
 * What an address has to have to be shown, whichever model it is from
 */
export interface IConfirmableAddress {
    addressLine1?   : string | null;
    addressLine2?   : string | null;
    city?           : string | null;
    stateProvince?  : string | null;
    postalCode?     : string | null;
    country?        : string | null;
    latitude?       : number | null;
    longitude?      : number | null;
    timeZoneId?     : string | null;
}

const hasLocation = (address: IConfirmableAddress) =>
    typeof address.latitude === 'number' && typeof address.longitude === 'number'
    && (address.latitude !== 0 || address.longitude !== 0);

interface ICardProps {
    title       : string;
    address     : IConfirmableAddress;
    isSuggested?: boolean;
    isSelected  : boolean;
    onSelect    : () => void;
}

const AddressCard = (props: ICardProps) => {

    const { address } = props;

    return <label className={ `suggested-address-card ${props.isSuggested ? 'suggested' : 'original'} ${props.isSelected ? 'selected' : ''}` }>
        <input
            type="radio"
            name="suggested-address-choice"
            className="suggested-address-radio"
            checked={ props.isSelected }
            onChange={ props.onSelect }
        />
        <h3>{ props.title }</h3>
        <address>
            <div className="suggested-address-line">{ address.addressLine1 }</div>
            { address.addressLine2 ? <div className="suggested-address-line">{ address.addressLine2 }</div> : null }
            <div className="suggested-address-line">
                { address.city }{ address.city && address.stateProvince ? ', ' : '' }{ address.stateProvince } { address.postalCode }
            </div>
            <div className="suggested-address-line">{ address.country }</div>
        </address>
        {
            hasLocation(address) || address.timeZoneId
                ? <div className="suggested-address-meta">
                    { hasLocation(address) ? <span>{ address.latitude!.toFixed(5) }, { address.longitude!.toFixed(5) }</span> : null }
                    { address.timeZoneId ? <span>{ address.timeZoneId }</span> : null }
                </div>
                : null
        }
    </label>;
};

interface IProps {
    original    : IConfirmableAddress;
    suggested   : IConfirmableAddress;
    useSuggested: boolean;
    onChoose    : (useSuggested: boolean) => void;
}

/**
 * The address that was entered next to the one Google suggested, to pick one of
 */
export const SuggestedAddress = (props: IProps) => {

    return <div className="suggested-address">
        <div className="suggested-address-intro">
            Google found a different address than the one entered. Select the address you want to use, then choose Save.
        </div>

        <div className="suggested-address-compare" role="radiogroup">
            <AddressCard
                title="As entered"
                address={ props.original }
                isSelected={ !props.useSuggested }
                onSelect={ () => props.onChoose(false) }
            />
            <AddressCard
                title="Suggested by Google"
                address={ props.suggested }
                isSuggested={ true }
                isSelected={ props.useSuggested }
                onSelect={ () => props.onChoose(true) }
            />
        </div>
    </div>;
};

interface IDialogState {
    isOpen      : boolean;
    original    : IConfirmableAddress;
    suggested   : IConfirmableAddress;
    useSuggested: boolean;
}

let resolve: (choice: boolean | null) => void = () => undefined;

class SuggestedAddressDialog extends React.Component<{}, IDialogState> {

    public static create() {

        const containerElement = document.createElement('div');
        document.body.appendChild(containerElement);

        return render(<SuggestedAddressDialog />, containerElement);
    }

    public state: IDialogState = { isOpen: false, original: {}, suggested: {}, useSuggested: true };

    public show = (original: IConfirmableAddress, suggested: IConfirmableAddress) => {

        // The suggested address is what is picked until the user says otherwise
        this.setState({ isOpen: true, original, suggested, useSuggested: true });

        return new Promise<boolean | null>((res) => {
            resolve = res;
        });
    }

    private close = (choice: boolean | null) => {
        this.setState({ isOpen: false });
        resolve(choice);
    }

    public render() {

        const { isOpen, original, suggested, useSuggested } = this.state;

        return <div className={ `result-dialog ${isOpen ? 'show' : ''}` }>
            <div className="result-dialog-backdrop" />
            <div className="result-dialog-content">
                <div className="result-dialog-modal">
                    <div className="result-dialog-header">
                        <h2>Address Confirmation</h2>
                    </div>
                    <div className="result-dialog-body">
                        <SuggestedAddress
                            original={ original }
                            suggested={ suggested }
                            useSuggested={ useSuggested }
                            onChoose={ (choice) => this.setState({ useSuggested: choice }) }
                        />
                    </div>
                    <div className="result-dialog-footer">
                        <button className="btn-flat positive" onClick={ () => this.close(this.state.useSuggested) }>Save</button>
                        <button className="btn-flat negative" onClick={ () => this.close(null) }>Cancel</button>
                    </div>
                </div>
            </div>
        </div>;
    }
}

const dialog = SuggestedAddressDialog.create() as unknown as SuggestedAddressDialog;

/**
 * Ask which address to save, the one Google suggested or the one that was entered
 * @param original The address as it was entered
 * @param suggested The address Google suggested
 * @returns true to use the suggested address, false to save the one entered, and null if the user cancelled
 */
export const showSuggestedAddressConfirmation = (original: IConfirmableAddress, suggested: IConfirmableAddress)
    : Promise<boolean | null> => dialog.show(original, suggested);
